import { useEffect, useRef, useState } from "react";

/**
 * RevealImage
 *  - Wraps an <img> in a reveal-fade container.
 *  - Applies the brand duotone via SVG filter (filter: url(#duotone-brand)).
 *  - Slow fade-in on enter (no slide, no bounce). Single trigger.
 *  - Honours prefers-reduced-motion: visible immediately.
 *
 * Use only with real assets supplied by the user. For not-yet-supplied
 * placements, render <AssetPlaceholder /> instead — never substitute stock
 * or generated imagery.
 */
export const RevealImage = ({
  src,
  alt,
  className = "",
  imgClassName = "",
  duotone = false, // photographs only — painted/watercolour assets keep their full colour
  blend,
  loading = "lazy",
  decoding = "async",
  fetchpriority,
  style,
  testId,
  children,
}) => {
  const wrapperRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const prefersReducedMotion = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)"
    ).matches;
    if (prefersReducedMotion) {
      setVisible(true);
      return undefined;
    }
    const node = wrapperRef.current;
    if (!node) return undefined;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -10% 0px" }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={wrapperRef}
      className={`reveal-fade ${visible ? "in" : ""} ${className}`}
      style={style}
      data-testid={testId}
    >
      <img
        src={src}
        alt={alt}
        loading={loading}
        decoding={decoding}
        {...(fetchpriority ? { fetchPriority: fetchpriority } : {})}
        className={`block w-full h-full object-cover ${duotone ? "duotone" : ""} ${imgClassName}`}
        style={blend ? { mixBlendMode: blend } : undefined}
      />
      {children}
    </div>
  );
};

/**
 * AssetPlaceholder
 *  - Honest Temple-Black field with a faint uppercase label naming the
 *    intended asset. NEVER replace with stock or AI imagery.
 */
export const AssetPlaceholder = ({
  label,
  aspect = "1 / 1",
  className = "",
  grain = true,
  testId,
}) => (
  <div
    className={`asset-placeholder ${grain ? "grain" : ""} ${className}`}
    style={{ aspectRatio: aspect }}
    data-testid={testId}
  >
    <span>{label}</span>
  </div>
);

export default RevealImage;
